// Editor Helpers - Consolidated utility functions for editor operations
import { useCallback, useEffect, useState } from 'react';
import { Editor } from '@tiptap/react';
import { FontFamily, FontSize, TextAlign } from '@/types';

import { logger } from '../../utils/logger';

type EditorFunction<T extends unknown[] = unknown[], R = unknown> = (editor: Editor, ...args: T) => R;
type CommandFunction = (chain: ReturnType<Editor['chain']>) => ReturnType<Editor['chain']>;

/**
 * Higher-order function that wraps editor operations with safety checks
 * @param fn - The function to wrap
 * @returns Wrapped function with editor safety checks
 */
export const withEditorCheck = <T extends unknown[] = unknown[], R = unknown>(fn: EditorFunction<T, R>): EditorFunction<T, R | undefined> => 
  (editor: Editor, ...args: T): R | undefined => {
    if (!editor) {
      if (import.meta.env.DEV) {
        logger.warn('Editor not initialized');
      }
      return;
    }
    if (!editor.isEditable) {
      if (import.meta.env.DEV) {
        logger.warn('Editor is not editable');
      }
      return;
    }
    return fn(editor, ...args);
  };

/**
 * Creates a safe editor command chain
 * @param editor - The editor instance
 * @returns Editor chain or null if editor is not available
 */
export const createSafeChain = (editor: Editor): ReturnType<Editor['chain']> | null => {
  if (!editor) return null;
  return editor.chain().focus();
};

/**
 * Executes a safe editor command
 * @param editor - The editor instance
 * @param command - The command to execute
 * @returns Success status
 */
export const executeSafeCommand = (editor: Editor, command: CommandFunction): boolean => {
  try {
    if (!editor) return false;
    const chain = createSafeChain(editor);
    if (!chain) return false;
    command(chain).run();
    return true;
  } catch (error) {
    if (import.meta.env.DEV) {
      logger.error('Editor command failed:', error instanceof Error ? error.message : 'Unknown error');
    }
    return false;
  }
};

/**
 * Table insertion helper
 * @param editor - The editor instance
 * @param rows - Number of rows
 * @param cols - Number of columns
 * @param withHeaderRow - Whether to include header row
 */
export const handleTableInsert = withEditorCheck((editor: Editor, rows: number, cols: number, withHeaderRow: boolean = true): void => {
  executeSafeCommand(editor, (chain) => 
    chain.insertTable({ rows, cols, withHeaderRow })
  );
  
  // Add paragraph after table for better UX
  executeSafeCommand(editor, (chain) => 
    chain.createParagraphNear()
  );
});

/**
 * Text formatting helpers
 */
export const textFormattingHelpers = {
  bold: withEditorCheck((editor: Editor): void => {
    executeSafeCommand(editor, (chain) => chain.toggleBold());
  }),
  
  italic: withEditorCheck((editor: Editor): void => {
    executeSafeCommand(editor, (chain) => chain.toggleItalic());
  }),
  
  underline: withEditorCheck((editor: Editor): void => {
    executeSafeCommand(editor, (chain) => chain.toggleUnderline());
  }),
  
  strikethrough: withEditorCheck((editor: Editor): void => {
    executeSafeCommand(editor, (chain) => chain.toggleStrike());
  }),
  
  highlight: withEditorCheck((editor: Editor): void => {
    executeSafeCommand(editor, (chain) => chain.toggleHighlight());
  }),
  
  setFontFamily: withEditorCheck((editor: Editor, fontFamily: FontFamily): void => {
    executeSafeCommand(editor, (chain) => chain.setMark('textStyle', { fontFamily }));
  }),
  
  setFontSize: withEditorCheck((editor: Editor, fontSize: FontSize): void => {
    executeSafeCommand(editor, (chain) => chain.setMark('textStyle', { fontSize }));
  }),
  
  setTextAlign: withEditorCheck((editor: Editor, align: TextAlign): void => {
    executeSafeCommand(editor, (chain) => chain.setTextAlign(align));
  }),
  
  setHeading: withEditorCheck((editor: Editor, level: 1 | 2 | 3 | 4 | 5 | 6): void => {
    executeSafeCommand(editor, (chain) => chain.toggleHeading({ level }));
  })
};

/**
 * List formatting helpers
 */
export const listFormattingHelpers = {
  bulletList: withEditorCheck((editor: Editor): void => {
    executeSafeCommand(editor, (chain) => chain.toggleBulletList());
  }),
  
  orderedList: withEditorCheck((editor: Editor): void => {
    executeSafeCommand(editor, (chain) => chain.toggleOrderedList());
  })
};

/**
 * Table operation helpers
 */
export const tableOperationHelpers = {
  insertRowBefore: withEditorCheck((editor: Editor): void => {
    executeSafeCommand(editor, (chain) => chain.addRowBefore());
  }),
  
  insertRowAfter: withEditorCheck((editor: Editor): void => {
    executeSafeCommand(editor, (chain) => chain.addRowAfter());
  }),
  
  insertColumnBefore: withEditorCheck((editor: Editor): void => {
    executeSafeCommand(editor, (chain) => chain.addColumnBefore());
  }),
  
  insertColumnAfter: withEditorCheck((editor: Editor): void => {
    executeSafeCommand(editor, (chain) => chain.addColumnAfter());
  }),
  
  deleteRow: withEditorCheck((editor: Editor): void => {
    executeSafeCommand(editor, (chain) => chain.deleteRow());
  }),
  
  deleteColumn: withEditorCheck((editor: Editor): void => {
    executeSafeCommand(editor, (chain) => chain.deleteColumn());
  }),
  
  deleteTable: withEditorCheck((editor: Editor): void => {
    executeSafeCommand(editor, (chain) => chain.deleteTable());
    executeSafeCommand(editor, (chain) => chain.insertContent({ type: 'paragraph' }));
  }),
  
  setCellAttribute: withEditorCheck((editor: Editor, attribute: string, value: string): void => {
    executeSafeCommand(editor, (chain) => chain.setCellAttribute(attribute, value));
  })
};

/**
 * History operation helpers
 */
export const historyHelpers = {
  undo: withEditorCheck((editor: Editor): void => {
    executeSafeCommand(editor, (chain) => chain.undo());
  }),
  
  redo: withEditorCheck((editor: Editor): void => {
    executeSafeCommand(editor, (chain) => chain.redo());
  })
};

/**
 * Link operation helpers
 */
export const linkHelpers = {
  setLink: withEditorCheck((editor: Editor, href: string): void => {
    executeSafeCommand(editor, (chain) => 
      chain.extendMarkRange('link').setLink({ href })
    );
  }),
  
  removeLink: withEditorCheck((editor: Editor): void => {
    executeSafeCommand(editor, (chain) => chain.unsetLink());
  })
};

/**
 * Insertion helpers
 */
export const insertionHelpers = {
  horizontalRule: withEditorCheck((editor: Editor): void => {
    executeSafeCommand(editor, (chain) => chain.setHorizontalRule());
  }),
  
  paragraph: withEditorCheck((editor: Editor): void => {
    executeSafeCommand(editor, (chain) => chain.setParagraph());
  }),
  
  hardBreak: withEditorCheck((editor: Editor): void => {
    executeSafeCommand(editor, (chain) => chain.setHardBreak());
  })
};

/**
 * React hook for safe editor command execution
 * @param editor - The editor instance
 * @param command - The command to execute
 * @returns Function to execute the command
 */
export const useSafeEditorCommand = (editor: Editor | null, command: CommandFunction) => {
  return useCallback(() => {
    if (!editor) return;
    executeSafeCommand(editor, command);
  }, [editor, command]);
};

/**
 * React hook for toolbar button handlers
 * @param editor - The editor instance
 * @param command - The command to execute
 * @param isActive - Whether the command is currently active
 * @returns Object with handler and active state
 */
export const useToolbarHandler = (editor: Editor | null, command: CommandFunction, isActive: boolean = false) => {
  const handler = useSafeEditorCommand(editor, command);
  
  return {
    handler,
    isActive,
    disabled: !editor || !editor.isEditable
  };
};

/**
 * Check if editor is ready for operations
 * @param editor - The editor instance
 * @returns Whether editor is ready
 */
export const isEditorReady = (editor: Editor | null): boolean => {
  return !!editor && editor.isEditable;
};

/**
 * Get current editor state
 * @param editor - The editor instance
 * @returns Editor state object
 */
export const getEditorState = (editor: Editor | null) => {
  if (!editor) return null;
  
  return {
    isEmpty: editor.isEmpty,
    isEditable: editor.isEditable,
    isDestroyed: editor.isDestroyed,
    can: editor.can,
    isActive: editor.isActive,
    getHTML: editor.getHTML,
    getJSON: editor.getJSON,
    getText: editor.getText
  };
};

/**
 * React hook for auto-saving editor content
 * @param editor - The editor instance
 * @param key - Storage key
 * @param onChange - Callback when content changes
 */
export const useAutoSave = (editor: Editor | null, key: string, onChange?: (content: string) => void) => {
  const [lastSaved, setLastSaved] = useState<string>('');
  
  useEffect(() => {
    if (!editor) return;
    
    const handleUpdate = () => {
      const content = editor.getHTML();
      if (content !== lastSaved) {
        localStorage.setItem(key, content);
        setLastSaved(content);
        onChange?.(content);
      }
    };
    
    editor.on('update', handleUpdate);
    return () => {
      editor.off('update', handleUpdate);
    };
  }, [editor, key, lastSaved, onChange]);
  
  // Load saved content on mount
  useEffect(() => {
    if (!editor) return;
    
    const saved = localStorage.getItem(key);
    if (saved && saved !== editor.getHTML()) {
      editor.commands.setContent(saved);
      setLastSaved(saved);
    }
  }, [editor, key]);
}; 